// The sonar sweep's pass (card 59, R548, ui-map-interface.md §17.3.59).
//
// It is the smallest pass in the client: one program, no buffer, no vertex
// array and one `drawArrays` per running reveal. A chart piece owns it,
// which is why it takes the RAW `WebGL2RenderingContext` out of a `DrawCtx`
// and not the `GL` wrapper the layers are built over: a piece is a singleton
// in the registry and holds no engine.
//
// **It builds itself lazily and it heals itself.** The first `draw` compiles
// the program and makes the four-corner vertex array; a lost context leaves
// both thrown away by the driver, so every draw asks `isProgram` and
// `isVertexArray` first and builds again when either answer is no. That is
// the whole of the context-loss story for a pass that owns no texture.
//
// The corner buffer is its own and not the shared one of `gl/corner.ts`,
// because the shared one belongs to a `GL` wrapper and this pass holds none.
// It reads the same four floats through the same two helpers (R553).
//
// `gfx/sweep.ts` is the dispatcher a piece talks to: it picks this class or
// the WebGPU one from `gfx.backend`, and it owns the two shapes below.

import { bindCorner, createCornerBuffer } from './corner';
import { SWEEP_FRAGMENT, SWEEP_VERTEX } from '../shaders/sweep';
import type { Sweep, SweepStyle } from '../gfx/sweep';
import type { CameraUniforms } from '../layer';

export class GlSweepPass {
  private program: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private vbo: WebGLBuffer | null = null;
  private gl: WebGL2RenderingContext | null = null;
  private loc = new Map<string, WebGLUniformLocation>();
  /** How many quads the last draw submitted. The test reads it. */
  draws = 0;

  /**
   * Draws every running sweep.
   *
   * `fade` is the chart's reading crossfade: the pass draws outside the
   * batches that carry it as a uniform, so it takes the same figure and
   * multiplies its own alpha by it.
   */
  draw(
    gl: WebGL2RenderingContext, cam: CameraUniforms,
    sweeps: readonly Sweep[], style: SweepStyle, fade: number,
  ): number {
    this.draws = 0;
    if (sweeps.length === 0) return 0;
    if (!this.ready(gl)) return 0;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    this.mat3('uViewProj', cam.viewProj);
    this.f4('uStopAt', style.stopAt);
    this.f4('uStopAlpha', style.stopAlpha);
    this.f2('uLine', style.lineAlpha, style.lineWidth);
    this.f1('uFade', fade);
    for (const s of sweeps) {
      if (s.r <= 0) continue;
      this.f2('uCentre', s.x, s.y);
      this.f1('uR', s.r);
      this.f1('uAngle', s.angle);
      this.f3('uRgb', s.rgb[0], s.rgb[1], s.rgb[2]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      this.draws++;
    }
    gl.bindVertexArray(null);
    return this.draws;
  }

  dispose(): void {
    if (this.gl) {
      if (this.program) this.gl.deleteProgram(this.program);
      if (this.vao) this.gl.deleteVertexArray(this.vao);
      if (this.vbo) this.gl.deleteBuffer(this.vbo);
    }
    this.program = null;
    this.vao = null;
    this.vbo = null;
    this.gl = null;
    this.loc.clear();
  }

  // -- the private half ------------------------------------------------------

  /**
   * Builds the program and the corner array the first time, and again after
   * a context loss.
   */
  private ready(gl: WebGL2RenderingContext): boolean {
    if (this.gl === gl && this.program && gl.isProgram(this.program)
      && this.vao && gl.isVertexArray(this.vao)) return true;
    // The objects of the context the pass had go first, as the WebGPU twin
    // disposes its pipeline (R1351).
    this.dispose();
    this.gl = gl;
    this.program = this.build(gl);
    this.vao = this.program ? this.corner(gl) : null;
    return this.program !== null && this.vao !== null;
  }

  /** The four-corner vertex array: one buffer at location 0, divisor 0. */
  private corner(gl: WebGL2RenderingContext): WebGLVertexArrayObject | null {
    const vao = gl.createVertexArray();
    const vbo = createCornerBuffer(gl);
    if (!vao || !vbo) {
      // A failing context made one of the two: free it, or it leaks.
      if (vao) gl.deleteVertexArray(vao);
      if (vbo) gl.deleteBuffer(vbo);
      return null;
    }
    gl.bindVertexArray(vao);
    bindCorner(gl, vbo);
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    this.vbo = vbo;
    return vao;
  }

  private build(gl: WebGL2RenderingContext): WebGLProgram | null {
    const vs = this.shader(gl, gl.VERTEX_SHADER, SWEEP_VERTEX);
    const fs = this.shader(gl, gl.FRAGMENT_SHADER, SWEEP_FRAGMENT);
    if (!vs || !fs) return null;
    const p = gl.createProgram();
    if (!p) return null;
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      gl.deleteProgram(p);
      return null;
    }
    return p;
  }

  private shader(gl: WebGL2RenderingContext, kind: number, src: string): WebGLShader | null {
    const s = gl.createShader(kind);
    if (!s) return null;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  /** The uniform's place, looked up one time and then held. */
  private at(name: string): WebGLUniformLocation | null {
    const held = this.loc.get(name);
    if (held) return held;
    const gl = this.gl, p = this.program;
    if (!gl || !p) return null;
    const l = gl.getUniformLocation(p, name);
    if (l) this.loc.set(name, l);
    return l;
  }

  private f1(name: string, a: number): void {
    const l = this.at(name);
    if (l) this.gl!.uniform1f(l, a);
  }

  private f2(name: string, a: number, b: number): void {
    const l = this.at(name);
    if (l) this.gl!.uniform2f(l, a, b);
  }

  private f3(name: string, a: number, b: number, c: number): void {
    const l = this.at(name);
    if (l) this.gl!.uniform3f(l, a, b, c);
  }

  private f4(name: string, v: readonly number[]): void {
    const l = this.at(name);
    if (l) this.gl!.uniform4f(l, v[0] ?? 0, v[1] ?? 0, v[2] ?? 0, v[3] ?? 0);
  }

  private mat3(name: string, m: Float32Array): void {
    const l = this.at(name);
    if (l) this.gl!.uniformMatrix3fv(l, false, m);
  }
}
